import React, { createContext, FC, ReactNode, useContext, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { CssBaseline, Stack, useMediaQuery } from "@mui/material";

import { useAuth } from "@hooks/auth";
import { useCountDownPlayback } from "@hooks/useCountDownPlayback";

import { limitDownloadsAmount } from "@store/actions/downloads";
import { getFavorites } from "@store/actions/favorites/favorites";
import { mergeServerCart } from "@store/reducers/cart";

import DevModalLauncher from "@components/DevModalLauncher";
import LoginDialog from "@components/LoginDialog";
import SignUpDialog from "@components/SignUpDialog";
import { UnauthorizedUserDialog } from "@components/UnauthorizedUserDialog";
import { PlayerContext } from "@layouts/PlayerLayout/utils";
import { IUser } from "@models/models";
import { AUTO_OPEN_LOGIN_KEY, ERoutes, LOGIN_NOTICES } from "@utils/constants";

import CatalogueBGDesktop from "@/assets/avif/catalog-desktop.avif";
import CatalogueBGMobile from "@/assets/avif/common-mobile.avif";
import CartModal from "@/components/CartModal";
import { StyledDivider } from "@/components/components";
import GlobalStyle from "@/components/GlobalStyle";
import { PendingEmailReminderProvider } from "@/contexts/PendingEmailReminderContext";
import BetaGlobalBanner from "@/layouts/components/BetaGlobalBanner";
import DevSwitcherRail from "@/layouts/components/DevSwitcherRail";
import LoggedInBanner, { LoggedInBannerSpacer } from "@/layouts/components/LoggedInBanner";
import MobileHeader from "@/layouts/components/MobileHeader";
import Footer from "@/layouts/components/SecondFooter";
import SecondHeader from "@/layouts/components/SecondHeader";
import { my } from "@/store/actions/user";
import { useAppDispatchUnwrap, useAppSelector } from "@/store/hooks";

type HandleOpenDialogType = (isLogin: boolean, isClosable?: boolean, isUnauthorized?: boolean, panel?: number) => void | null;

export const HandleOpenDialog = createContext<HandleOpenDialogType>(null);

interface ISecondRoot {
  isNewsletter?: boolean;
  children: ReactNode;
}

export enum ERecovery {
  FirstStep = 1,
  SecondStep = 2,
  ThirdStep = 3,
}

export enum ESignUp {
  VerifyEmail = 0,
  Welcome,
  AcceptTerms,
  Newsletter,
  Links,
  ClassifyContent,
  UseCases,
}

enum ESignUpFlow {
  OpenDialog = "openDialog",
  OpenSignUpDialog = "OpenSignUpDialog",
  AuthPanel = "authPanel",
  SignUpStep = "signUpStep",
  IS_CLOSABLE = "IS_CLOSABLE",
  UNAUTHORIZED_USER_DIALOG = "UNAUTHORIZED_USER_DIALOG",
}

type SignUpFlowAction =
  | {
      type:
        | ESignUpFlow.OpenDialog
        | ESignUpFlow.OpenSignUpDialog
        | ESignUpFlow.IS_CLOSABLE
        | ESignUpFlow.UNAUTHORIZED_USER_DIALOG;
      payload: boolean;
    }
  | {
      type: ESignUpFlow.AuthPanel | ESignUpFlow.SignUpStep;
      payload: number;
    };

interface SignUpFlowState {
  isDialogOpen: boolean;
  authPanel: number;
  isSignUpDialogOpen: boolean;
  isClosable: boolean;
  signUpStep: number;
  unauthorizedDialogOpen: boolean;
}

const signUpFlowReducer = (state: SignUpFlowState, action: SignUpFlowAction) => {
  const { type, payload } = action;
  switch (type) {
    case ESignUpFlow.OpenDialog:
      return {
        ...state,
        isDialogOpen: payload,
      };
    case ESignUpFlow.OpenSignUpDialog:
      return {
        ...state,
        isSignUpDialogOpen: payload,
      };
    case ESignUpFlow.AuthPanel:
      return {
        ...state,
        authPanel: payload,
      };
    case ESignUpFlow.SignUpStep:
      return {
        ...state,
        signUpStep: payload,
      };
    case ESignUpFlow.IS_CLOSABLE:
      return {
        ...state,
        isClosable: payload,
      };
    case ESignUpFlow.UNAUTHORIZED_USER_DIALOG:
      return {
        ...state,
        unauthorizedDialogOpen: payload,
      };
    default:
      return state;
  }
};

const signUpStep = (me: IUser) => {
  switch (true) {
    // case !me.socialLinks:
    //   return ESignUp.Links;
    // case !me.categories.length:
    //   return ESignUp.ClassifyContent;
    // case !me.useCases:
    //   return ESignUp.UseCases;
    // case !me.hasAcceptedTerms:
    //   return ESignUp.AcceptTerms;
    // case !me.hasNewsletter:
    //   return ESignUp.Newsletter;
    case !me.isEmailVerified:
      return ESignUp.VerifyEmail;
    default:
      return ESignUp.Links;
  }
};

const SecondRootLayout: FC<ISecondRoot> = ({ children, isNewsletter = true }) => {
  useTranslation(["navigation", "questionFlow"]);
  // Header swap boundary is 1150 (not the global md=1024): between 1024–1150 the
  // desktop header's nav overlaps the cart in logged-out mode, so collapse to the
  // mobile/tablet shell earlier. MobileHeader's own phone↔tablet split stays at 600.
  const isDesktop = useMediaQuery("(min-width:1150px)");
  const { isUserCanPlay } = useCountDownPlayback();

  const { myData } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // When the user signs up via the cart-modal signup gate, the cart is
  // already open with a subscription config the user explicitly chose
  // BEFORE signing up. We don't want to: (a) navigate them away to
  // /music (loses context), or (b) park them on the generic Welcome
  // onboarding card (the plan-config that's waiting in the cart is the
  // more urgent next step). Both behaviours stay intact for normal
  // signups (header → Sign up) where `cart.isOpen` is false.
  const isCartOpen = useAppSelector((s) => s.cart.isOpen);

  const [state, dispatchReducer] = useReducer(signUpFlowReducer, {
    isDialogOpen: false,
    authPanel: 0,
    isSignUpDialogOpen: false,
    signUpStep: ESignUp.VerifyEmail,
    isClosable: isUserCanPlay,
    unauthorizedDialogOpen: false,
  });
  const { openLoginDialog, setOpenLoginDialog } = useContext(PlayerContext);

  const token = searchParams.get("token");
  const location = useLocation();
  // Paths that legitimately use ?token=... for non-auth purposes — they
  // sign their own JWTs for page-specific flows (newsletter preferences,
  // etc.) and MUST NOT trigger the generic auth dialog. Extend as more
  // token-carrying pages appear.
  const isNonAuthTokenPath =
    location.pathname.startsWith("/newsletter/preferences") ||
    location.pathname.startsWith("/unsubscribe");

  const dispatch = useAppDispatchUnwrap();

  useEffect(() => {
    const getData = async () => {
      // allSettled, not all — and the catch below is not defensive padding.
      //
      // All three ask a question only a signed-in visitor can answer: who am I,
      // what are my favourites, how many downloads have I left. A guest gets 401
      // from each, which is CORRECT. But `useAppDispatchUnwrap` calls `.unwrap()`,
      // which re-throws, so `Promise.all` rejected and nothing caught it — an
      // unhandled promise rejection on EVERY logged-out page load. Invisible in a
      // browser (a console line nobody reads); fatal to react-snap, which treats a
      // page error as a reason to abandon the entire prerender. That is what broke
      // the first two prod frontend builds.
      //
      // allSettled also fixes a smaller bug hiding behind it: with `all`, one
      // expected 401 cancelled the others AND skipped `mergeServerCart` entirely.
      await Promise.allSettled([dispatch(my()), dispatch(getFavorites()), dispatch(limitDownloadsAmount())]);
      // Reconcile the local cart with the account's server cart (no-op for guests).
      dispatch(mergeServerCart());
    };
    getData().catch(() => {
      // Nothing above is required for an anonymous visitor to browse the site.
    });

    // Preload shared background images so they're cached across page transitions
    [CatalogueBGDesktop, CatalogueBGMobile].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if (!myData) return;
    if (
      !myData.isEmailVerified
      // !myData.socialLinks ||
      // !myData.categories.length ||
      // !myData.useCases ||
      // !myData.hasAcceptedTerms ||
      // !myData.hasNewsletter
    ) {
      if (!myData.isEmailVerified) {
        // Skip the catch-all redirect to /music when the user signed up
        // via the cart-modal gate — they're mid-checkout on a page they
        // came from (Pricing / home) and the cart needs to stay in view.
        if (!isCartOpen) {
          navigate(ERoutes.Catalog);
        }
      }

      dispatchReducer({
        type: ESignUpFlow.SignUpStep,
        payload: signUpStep(myData),
      });

      dispatchReducer({
        type: ESignUpFlow.OpenSignUpDialog,
        payload: true,
      });
    }
  }, [
    myData?.isEmailVerified,
    myData?.socialLinks,
    JSON.stringify(myData?.socialLinks),
    JSON.stringify(myData?.categories),
    JSON.stringify(myData?.useCases),
    myData?.hasAcceptedTerms,
    myData?.hasNewsletter,
  ]);

  useEffect(() => {
    if (state.signUpStep > ESignUp.Welcome) {
      dispatchReducer({
        type: ESignUpFlow.OpenSignUpDialog,
        payload: false,
      });
    }
  }, [state.signUpStep]);

  useEffect(() => {
    if (token && !isNonAuthTokenPath) {
      dispatchReducer({
        type: ESignUpFlow.AuthPanel,
        payload: 4,
      });
      dispatchReducer({
        type: ESignUpFlow.OpenDialog,
        payload: true,
      });
    }
  }, [token, isNonAuthTokenPath]);

  // Green notice shown above the login form, taken from `/?login=<code>`.
  const [loginNotice, setLoginNotice] = useState("");

  const handleOpenDialog = (isLogin: boolean, isClosable?: boolean, isUnauthorized?: boolean, panel?: number) => {
    if (isUnauthorized) {
      return dispatchReducer({
        type: ESignUpFlow.UNAUTHORIZED_USER_DIALOG,
        payload: true,
      });
    }
    dispatchReducer({
      type: ESignUpFlow.UNAUTHORIZED_USER_DIALOG,
      payload: false,
    });
    if (!isClosable) {
      dispatchReducer({
        type: ESignUpFlow.IS_CLOSABLE,
        payload: isClosable,
      });
    }
    dispatchReducer({
      type: ESignUpFlow.AuthPanel,
      payload: panel !== undefined ? panel : (isLogin ? 0 : 1),
    });
    dispatchReducer({
      type: ESignUpFlow.OpenDialog,
      payload: true,
    });
  };

  useEffect(() => {
    if (openLoginDialog) {
      handleOpenDialog(false, true, true);
    }
  }, [openLoginDialog]);

  // Open the login modal from the URL: `/?login=<code>`.
  //
  // The query param IS the state, deliberately. This used to be a
  // sessionStorage handshake (AUTO_OPEN_LOGIN_KEY) that opened the modal and
  // then lost it — the key was consumed on read, this layout re-mounts during
  // navigation, and the re-mounted layout had no idea the modal should be
  // open, so it flashed up and vanished. A param survives any number of
  // re-mounts because the effect just re-reads it. It is stripped only when
  // the user dismisses the dialog (see LoginDialog's handleClose below).
  //
  // A known code also selects the green notice shown above the form, so the
  // copy travels in the URL rather than in a second one-shot storage key.
  // `?signup=1` is the sibling deep link from the Day-30
  // account_permanently_deleted email's "Create New Account" CTA.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const signup = url.searchParams.get("signup");
      const login = url.searchParams.get("login");
      if (signup && signup !== "0" && signup !== "false") {
        handleOpenDialog(false, true, false, 1); // signup panel
        url.searchParams.delete("signup");
        window.history.replaceState({}, "", url.pathname + (url.search ? `?${url.searchParams.toString()}` : ""));
      } else if (login && login !== "0" && login !== "false") {
        // Already signed in (e.g. the full reload after a successful login) —
        // the request is stale, so drop it instead of popping a login modal at
        // someone who just logged in.
        if (localStorage.getItem("tokens")) {
          url.searchParams.delete("login");
          window.history.replaceState({}, "", url.pathname + (url.search ? `?${url.searchParams.toString()}` : ""));
        } else {
          setLoginNotice(LOGIN_NOTICES[login] ?? "");
          handleOpenDialog(true, true, false, 0); // login panel
          // NOT stripped here — that is what made the old version fragile.
        }
      }
      // Legacy sessionStorage trigger, kept so anything still setting it keeps
      // working. Same rule: consume it only once the dialog has been dismissed.
      if (!login && sessionStorage.getItem(AUTO_OPEN_LOGIN_KEY) && !localStorage.getItem("tokens")) {
        handleOpenDialog(true, true, false, 0);
      }
    } catch { /* malformed URL / storage blocked — user can click Login */ }
    // Intentionally fires once on mount; no deps.
    // eslint-disable-next-line
  }, []);

  // Programmatic "open login dialog" signal — any deep child can dispatch
  // `window.dispatchEvent(new CustomEvent("as:open-login"))` and we'll pop
  // the login panel here (closable, not marked unauthorized). Used by the
  // `/account-deleted` page's "Login to Recover Account" CTA so the modal
  // opens in-place without navigating away from the page.
  useEffect(() => {
    const onOpenLogin = () => handleOpenDialog(true, true, false, 0);
    window.addEventListener("as:open-login", onOpenLogin as EventListener);
    return () => window.removeEventListener("as:open-login", onOpenLogin as EventListener);
    // eslint-disable-next-line
  }, []);

  const setAuthPanel = (panel: number) => {
    dispatchReducer({
      type: ESignUpFlow.AuthPanel,
      payload: panel,
    });
  };

  const handleNextStepSignUp = () => {
    // After VerifyEmail succeeds, normally signUpStep advances to Welcome
    // (the onboarding card). But if the user signed up via the cart-modal
    // gate, the cart is open with a subscription config waiting for
    // plan-config — Welcome is the wrong destination, it'd hide the cart.
    // Skip past Welcome straight to Links so the "step > Welcome" effect
    // below closes the dialog and reveals the cart underneath.
    const skipWelcome = isCartOpen && state.signUpStep === ESignUp.VerifyEmail;
    const next = skipWelcome ? ESignUp.Links : state.signUpStep + 1;
    dispatchReducer({
      type: ESignUpFlow.SignUpStep,
      payload: next,
    });
  };

  return (
    <PendingEmailReminderProvider>
      <CssBaseline />
      <GlobalStyle />

      <Stack width="100%" sx={{ minHeight: "100vh" }}>
        {isDesktop && <SecondHeader handleOpenDialog={handleOpenDialog} />}

        {!isDesktop && <MobileHeader handleOpenDialog={handleOpenDialog} />}

        {/* Banner shown only on /account/* pages (excluding
            /account/claims). Renders inline in flow — on those pages
            the header is static / non-fading, so both the header and
            this banner scroll away naturally with page content. */}
        {isDesktop && <LoggedInBanner />}
        <LoggedInBannerSpacer />
        {/* Pending-email site-wide banner now lives INSIDE SecondHeader so
            the fixed header positioning doesn't cover it on non-/account
            pages. See SecondHeader.tsx for the mount. */}

        {/* Fixed left rail of dev-only switchers (Account / User / Auth
            / Bg-effect). Hidden in production builds. The background-
            effect layer itself mounts INSIDE each page's hero — see
            HomePage/Hero, etc. */}
        <DevSwitcherRail />

        <div style={{ flex: 1 }}>
          <HandleOpenDialog.Provider
            value={(isLogIn, isClosable, isUnauthorized, panel) => {
              handleOpenDialog(isLogIn, isClosable, isUnauthorized, panel);
            }}
          >
            {children}
            <CartModal />
          </HandleOpenDialog.Provider>
        </div>

        {/* Closes the page. Every section boundary on the site draws this seam,
            and the last one — page to footer — was the only one that did not.
            Here rather than in the footer so it belongs to the content it ends,
            and above the dismissed beta notice so the notice sits below the
            line with the footer rather than floating between two sections. */}
        <StyledDivider $isSection sx={{ position: "relative !important" }} />

        {/* Where the beta notice goes once it is dismissed from the header:
            the seam between the page and the footer, on every page. Outside
            the content div on purpose — inside, it would inherit each page's
            own width and padding and land at a different place on each one. */}
        <BetaGlobalBanner placement="bottom" />

        <Footer isNewsletter={isNewsletter} />
      </Stack>

      <LoginDialog
        open={state.isDialogOpen}
        isClosable={state.isClosable}
        authPanel={state.authPanel}
        setAuthPanel={setAuthPanel}
        resetToken={token}
        loginNotice={loginNotice}
        handleClose={() => {
          // The request is honoured once the user has actually seen the dialog
          // and dismissed it — only now is it safe to drop the trigger, since
          // until this point a re-mount still needs to be able to re-open.
          try {
            const url = new URL(window.location.href);
            if (url.searchParams.has("login")) {
              url.searchParams.delete("login");
              window.history.replaceState({}, "", url.pathname + (url.search ? `?${url.searchParams.toString()}` : ""));
            }
            sessionStorage.removeItem(AUTO_OPEN_LOGIN_KEY);
          } catch { /* malformed URL / storage blocked — noop */ }
          setLoginNotice("");
          dispatchReducer({
            type: ESignUpFlow.OpenDialog,
            payload: false,
          });
          // Intentionally NOT resetting authPanel here — see feedback_modal_blur_fix
          // memory: "step freeze on close". The MUI Dialog fade-out animation takes
          // ~225ms; mutating authPanel synchronously makes the slider jump to panel 0
          // mid-fade, breaking the visual. handleOpenDialog already sets the right
          // panel on the next open (line 279 in this file), so the reset here was
          // redundant. Same rationale for viewport height inside LoginDialog.
          if (openLoginDialog) {
            setOpenLoginDialog();
          }
        }}
      />

      <SignUpDialog
        open={state.isSignUpDialogOpen}
        handleClose={() => {
          dispatchReducer({
            type: ESignUpFlow.OpenSignUpDialog,
            payload: false,
          });
        }}
        handleNextStep={handleNextStepSignUp}
        step={state.signUpStep}
      />

      <UnauthorizedUserDialog
        open={state.unauthorizedDialogOpen}
        handleClose={() => {
          dispatchReducer({
            type: ESignUpFlow.UNAUTHORIZED_USER_DIALOG,
            payload: false,
          });
          if (openLoginDialog) {
            setOpenLoginDialog();
          }
        }}
        handleLogin={() => handleOpenDialog(true)}
        handleSignUp={() => handleOpenDialog(false)}
      />

      {/* Dev-only: open flow-gated modals directly via ?devmodal=… (dev host
          only). Lets us review verify/welcome/delete-code/password-code on the
          live dev site without walking the full flow. */}
      <DevModalLauncher />
    </PendingEmailReminderProvider>
  );
};

export default SecondRootLayout;
